import { useState } from 'react';
import { Building2, Warehouse, Tag, Home, FileText, ArrowLeft, ArrowRight, CheckCircle2, Loader2 } from 'lucide-react';
import { useCreateLocation } from '@/hooks/use-locations';
import { useLocationStore } from '@/stores/location-store';
import { toast } from '@/stores/toast-store';

const inputBase = 'h-10 w-full rounded-lg border bg-white px-3 text-sm outline-none focus:ring-1';
const inputNormal = `${inputBase} border-input focus:border-primary focus:ring-primary`;
const inputError = `${inputBase} border-red-500 focus:border-red-500 focus:ring-red-500`;

type FieldErrors = Record<string, string>;

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p className="mt-1 text-xs text-red-500" role="alert">
      {message}
    </p>
  );
}

function fc(fieldErrors: FieldErrors, field: string): string {
  return fieldErrors[field] ? inputError : inputNormal;
}

const STEPS = [
  { key: 'identity', label: 'Identity', icon: Tag },
  { key: 'address', label: 'Address', icon: Home },
  { key: 'review', label: 'Review & Create', icon: FileText },
] as const;

type StepKey = (typeof STEPS)[number]['key'];
const STEP_ORDER: StepKey[] = ['identity', 'address', 'review'];

interface CompanyDraft {
  name: string;
  nickname: string;
  address: string;
  /** Contact email vendors reach the company at (PO Reply-To). Optional. */
  email: string;
  type: 'company' | 'warehouse';
}

function IdentityStep({
  draft,
  errors,
  onChange,
}: {
  draft: CompanyDraft;
  errors: FieldErrors;
  onChange: (field: keyof CompanyDraft, value: string) => void;
}) {
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold">Location Identity</h2>
        <p className="text-sm text-muted-foreground">Choose the type and give this location a name.</p>
      </div>

      <div>
        <label className="mb-1.5 block text-sm font-medium">Location Type</label>
        <div className="flex gap-3">
          {[
            {
              value: 'company' as const,
              label: 'Company',
              icon: Building2,
              desc: 'Standard retail, restaurant, startup company, business, non-profit etc.',
            },
            {
              value: 'warehouse' as const,
              label: 'Warehouse',
              icon: Warehouse,
              desc: 'Warehouse, vendor, manufacturer, distributors etc.',
            },
          ].map((opt) => {
            const Icon = opt.icon;
            const selected = draft.type === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => onChange('type', opt.value)}
                className={`flex flex-1 items-center gap-3 rounded-lg border-2 p-3 text-left transition-colors ${
                  selected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'
                }`}
              >
                <Icon className={`h-5 w-5 ${selected ? 'text-primary' : 'text-muted-foreground'}`} />
                <div>
                  <div className={`text-sm font-medium ${selected ? 'text-primary' : ''}`}>{opt.label}</div>
                  <div className="text-xs text-muted-foreground">{opt.desc}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label className="mb-1.5 block text-sm font-medium">
          {draft.type === 'warehouse' ? 'Warehouse Name' : 'Company Name'} <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          placeholder="e.g. Downtown Branch"
          value={draft.name}
          onChange={(e) => onChange('name', e.target.value)}
          className={fc(errors, 'name')}
          autoFocus
        />
        <FieldError message={errors.name} />
      </div>

      <div>
        <label className="mb-1.5 block text-sm font-medium">
          Nickname <span className="text-xs text-muted-foreground">(shown in header)</span>
        </label>
        <input
          type="text"
          placeholder="e.g. Downtown"
          value={draft.nickname}
          onChange={(e) => onChange('nickname', e.target.value)}
          className={fc(errors, 'nickname')}
          maxLength={30}
        />
        <p className="mt-1 text-xs text-muted-foreground">
          A short label displayed in the top navigation bar. Leave blank to use the full name.
        </p>
        <FieldError message={errors.nickname} />
      </div>
    </div>
  );
}

function AddressStep({
  draft,
  errors,
  onChange,
}: {
  draft: CompanyDraft;
  errors: FieldErrors;
  onChange: (field: keyof CompanyDraft, value: string) => void;
}) {
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold">Address</h2>
        <p className="text-sm text-muted-foreground">Provide the physical address for this company/location.</p>
      </div>

      <div>
        <label className="mb-1.5 block text-sm font-medium">Address</label>
        <input
          type="text"
          placeholder="e.g. 123 Main St, Springfield, IL"
          value={draft.address}
          onChange={(e) => onChange('address', e.target.value)}
          className={fc(errors, 'address')}
        />
        <FieldError message={errors.address} />
      </div>

      <div>
        <label className="mb-1.5 block text-sm font-medium">Contact Email</label>
        <input
          type="email"
          placeholder="e.g. orders@yourstore.com"
          value={draft.email}
          onChange={(e) => onChange('email', e.target.value)}
          className={fc(errors, 'email')}
        />
        <p className="mt-1 text-xs text-muted-foreground">
          Vendors reply to this address on purchase orders. Leave blank to use your registration email.
        </p>
        <FieldError message={errors.email} />
      </div>
    </div>
  );
}

function ReviewRow({
  label,
  value,
  step,
  onEdit,
}: {
  label: string;
  value: string;
  step: StepKey;
  onEdit: (step: StepKey) => void;
}) {
  return (
    <div className="flex items-center justify-between border-b border-border py-3 last:border-0">
      <div>
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-sm font-medium">{value || <span className="text-muted-foreground">—</span>}</div>
      </div>
      <button type="button" onClick={() => onEdit(step)} className="text-xs text-primary hover:underline">
        Edit
      </button>
    </div>
  );
}

function ReviewStep({ draft, onEdit }: { draft: CompanyDraft; onEdit: (step: StepKey) => void }) {
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold">Review &amp; Create</h2>
        <p className="text-sm text-muted-foreground">
          Review the details below. After creating, you'll select modules and complete the business registration.
        </p>
      </div>

      <div className="divide-y divide-border rounded-lg border border-border bg-muted/20 px-4">
        <ReviewRow
          label="Type"
          value={draft.type === 'warehouse' ? 'Warehouse' : 'Company'}
          step="identity"
          onEdit={onEdit}
        />
        <ReviewRow
          label={draft.type === 'warehouse' ? 'Warehouse Name' : 'Company Name'}
          value={draft.name}
          step="identity"
          onEdit={onEdit}
        />
        <ReviewRow label="Nickname" value={draft.nickname} step="identity" onEdit={onEdit} />
        <ReviewRow label="Address" value={draft.address} step="address" onEdit={onEdit} />
        <ReviewRow label="Contact Email" value={draft.email} step="address" onEdit={onEdit} />
      </div>
    </div>
  );
}

export function AddCompanyPage() {
  const [stepKey, setStepKey] = useState<StepKey>('identity');
  const [draft, setDraft] = useState<CompanyDraft>({ name: '', nickname: '', address: '', email: '', type: 'company' });
  const [errors, setErrors] = useState<FieldErrors>({});

  const createLocation = useCreateLocation();
  const { setSelectedLocation } = useLocationStore();

  const stepIndex = STEP_ORDER.indexOf(stepKey);

  function onChange(field: keyof CompanyDraft, value: string) {
    setDraft((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  }

  function validateIdentity(): FieldErrors {
    const e: FieldErrors = {};
    if (!draft.name.trim()) e.name = 'Company name is required.';
    if (draft.nickname.trim().length > 30) e.nickname = 'Nickname must be 30 characters or fewer.';
    return e;
  }

  function goNext() {
    let errs: FieldErrors = {};
    if (stepKey === 'identity') errs = validateIdentity();

    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    const next = STEP_ORDER[stepIndex + 1];
    if (next) setStepKey(next);
  }

  function goBack() {
    const prev = STEP_ORDER[stepIndex - 1];
    if (prev) setStepKey(prev);
  }

  async function handleCreate() {
    const errs = validateIdentity();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    try {
      const res = await createLocation.mutateAsync({
        name: draft.name.trim(),
        nickname: draft.nickname.trim() || undefined,
        address: draft.address.trim() || undefined,
        email: draft.email.trim() || undefined,
        type: draft.type,
      });

      const created = res?.data;
      if (created?.id) {
        setSelectedLocation(created.id, created.name);
        toast.success(`Company "${draft.name.trim()}" created. Select your modules to continue.`);
        // Navigate to module selection — mirrors the initial tenant onboarding flow
        window.location.hash = '#/modules';
      }
    } catch {
      // Error is handled globally by the QueryClient onError
    }
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6 py-8 px-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div
          className={`flex h-10 w-10 items-center justify-center rounded-full ${
            draft.type === 'warehouse' ? 'bg-orange-100' : 'bg-primary/10'
          }`}
        >
          {draft.type === 'warehouse' ? (
            <Warehouse className="h-5 w-5 text-orange-600" />
          ) : (
            <Building2 className="h-5 w-5 text-primary" />
          )}
        </div>
        <div>
          <h1 className="text-xl font-semibold">Add New {draft.type === 'warehouse' ? 'Warehouse' : 'Company'}</h1>
          <p className="text-sm text-muted-foreground">
            Set up a new {draft.type === 'warehouse' ? 'warehouse' : 'company'} location under your account
          </p>
        </div>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-0">
        {STEPS.map((step, idx) => {
          const Icon = step.icon;
          const isCompleted = idx < stepIndex;
          const isActive = idx === stepIndex;
          return (
            <div key={step.key} className="flex flex-1 items-center">
              <div className="flex flex-col items-center gap-1">
                <div
                  className={`flex h-9 w-9 items-center justify-center rounded-full border-2 transition-colors ${
                    isCompleted
                      ? 'border-primary bg-primary text-primary-foreground'
                      : isActive
                        ? 'border-primary bg-white text-primary'
                        : 'border-border bg-white text-muted-foreground'
                  }`}
                >
                  {isCompleted ? <CheckCircle2 className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                </div>
                <span
                  className={`text-xs font-medium ${
                    isActive ? 'text-primary' : isCompleted ? 'text-primary/70' : 'text-muted-foreground'
                  }`}
                >
                  {step.label}
                </span>
              </div>
              {idx < STEPS.length - 1 && (
                <div className={`h-0.5 flex-1 mx-2 mb-4 ${idx < stepIndex ? 'bg-primary' : 'bg-border'}`} />
              )}
            </div>
          );
        })}
      </div>

      {/* Step content */}
      <div className="rounded-xl border border-border bg-white p-6 shadow-sm">
        {stepKey === 'identity' && <IdentityStep draft={draft} errors={errors} onChange={onChange} />}
        {stepKey === 'address' && <AddressStep draft={draft} errors={errors} onChange={onChange} />}
        {stepKey === 'review' && <ReviewStep draft={draft} onEdit={setStepKey} />}

        {/* Navigation */}
        <div className="mt-8 flex items-center justify-between">
          <button
            type="button"
            onClick={
              stepIndex === 0
                ? () => {
                    window.location.hash = '#/';
                  }
                : goBack
            }
            className="flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
          >
            <ArrowLeft className="h-4 w-4" />
            {stepIndex === 0 ? 'Cancel' : 'Back'}
          </button>

          {stepKey !== 'review' ? (
            <button
              type="button"
              onClick={goNext}
              className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              Next
              <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleCreate}
              disabled={createLocation.isPending}
              className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {createLocation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Creating…
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4" />
                  Create {draft.type === 'warehouse' ? 'Warehouse' : 'Company'}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
